<template>
  <div>
    <!-- Botón para abrir modal de incidencia -->
    <base-button
      size="sm"
      type="warning"
      @click="openIncidenceModal"
      :disabled="!canReportIncidence"
      title="Reportar Incidencia"
      class="incidence-button"
    >
      <i class="fas fa-exclamation-triangle"></i>
    </base-button>

    <!-- Modal de Incidencia -->
    <modal
      :show.sync="showIncidenceModal"
      size="md"
      @close="closeIncidenceModal"
    >
      <template v-slot:header>
        <h3 class="modal-title">Reportar Incidencia de Material</h3>
      </template>
      <div class="modal-body">
        <div class="alert alert-warning">
          <i class="fas fa-exclamation-triangle mr-2"></i>
          <strong>Material:</strong> {{ material?.activo?.numero_serie || material?.descripcion }}
        </div>
        
        <div class="form-group">
          <label class="form-control-label">Tipo de Incidencia</label>
          <select 
            v-model="incidenceData.tipo" 
            class="form-control"
            required
          >
            <option value="">Seleccionar tipo de incidencia</option>
            <option value="dañado">Material Dañado</option>
            <option value="perdido">Material Perdido</option>
            <option value="defectuoso">Material Defectuoso</option>
            <option value="incompleto">Material Incompleto</option>
            <option value="otro">Otro</option>
          </select>
        </div>

        <div class="form-group">
          <label class="form-control-label">Descripción de la Incidencia</label>
          <textarea
            v-model="incidenceData.descripcion"
            class="form-control"
            rows="4"
            placeholder="Describe detalladamente la incidencia..."
            required
          ></textarea>
        </div>

        <div class="form-group">
          <label class="form-control-label">Acción Requerida</label>
          <select 
            v-model="incidenceData.accion" 
            class="form-control"
            required
          >
            <option value="">Seleccionar acción requerida</option>
            <option value="reparar">Reparar</option>
            <option value="reemplazar">Reemplazar</option>
            <option value="devolver">Devolver</option>
            <option value="investigar">Investigar</option>
            <option value="otro">Otro</option>
          </select>
        </div>

        <div class="form-group">
          <label class="form-control-label">Prioridad</label>
          <select 
            v-model="incidenceData.prioridad" 
            class="form-control"
            required
          >
            <option value="baja">Baja</option>
            <option value="media">Media</option>
            <option value="alta">Alta</option>
            <option value="critica">Crítica</option>
          </select>
        </div>
      </div>
      <template v-slot:footer>
        <base-button
          type="secondary"
          @click="closeIncidenceModal"
        >
          Cancelar
        </base-button>
        <base-button
          type="warning"
          @click="submitIncidence"
          :loading="submitting"
          :disabled="!isValidIncidence"
        >
          <i class="fas fa-exclamation-triangle mr-2"></i>
          Reportar Incidencia
        </base-button>
      </template>
    </modal>
  </div>
</template>

<script>
import axios from 'axios';
import Modal from "@/components/Modal";

export default {
  name: "albaran-material-incidence",
  components: {
    Modal
  },
  props: {
    material: {
      type: Object,
      required: true
    },
    albaranId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      showIncidenceModal: false,
      submitting: false,
      incidenceData: {
        tipo: '',
        descripcion: '',
        accion: '',
        prioridad: 'media'
      }
    };
  },
  computed: {
    canReportIncidence() {
      // Solo se puede reportar incidencia si el material no está ya en estado incidencia
      return this.material && this.material.estado !== 'incidencia';
    },
    isValidIncidence() {
      return this.incidenceData.tipo && 
             this.incidenceData.descripcion && 
             this.incidenceData.accion && 
             this.incidenceData.prioridad;
    }
  },
  methods: {
    openIncidenceModal() {
      this.resetIncidenceData();
      this.showIncidenceModal = true;
    },

    closeIncidenceModal() {
      this.showIncidenceModal = false;
      this.resetIncidenceData();
    },

    resetIncidenceData() {
      this.incidenceData = {
        tipo: '',
        descripcion: '',
        accion: '',
        prioridad: 'media'
      };
    },

    async submitIncidence() {
      if (!this.isValidIncidence) {
        this.$notify({
          type: "warning",
          message: "Por favor, completa todos los campos requeridos"
        });
        return;
      }

      try {
        this.submitting = true;

        const apiUrl = process.env.VUE_APP_API_URL || 'https://misepis-api.delfromsoftware.com/api';
        
        // Actualizar el estado del material a incidencia
        await axios.put(
          `${apiUrl}/albaran-activo-materials/${this.material.id}`,
          {
            data: {
              estado: 'incidencia',
              observaciones: `INCIDENCIA REPORTADA:
Tipo: ${this.incidenceData.tipo}
Descripción: ${this.incidenceData.descripcion}
Acción requerida: ${this.incidenceData.accion}
Prioridad: ${this.incidenceData.prioridad}
Fecha: ${new Date().toISOString()}
Usuario: ${localStorage.getItem('userName') || 'Usuario'}`,
              fecha_accion: new Date().toISOString()
            }
          },
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );

        this.$notify({
          type: "success",
          message: "Incidencia reportada correctamente"
        });

        this.closeIncidenceModal();
        
        // Emitir evento para que el componente padre actualice los datos
        this.$emit('incidence-reported');

      } catch (error) {
        console.error("Error reportando incidencia:", error);
        this.$notify({
          type: "danger",
          message: "Error al reportar la incidencia: " + (error.response?.data?.error?.message || "Error desconocido")
        });
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>

<style scoped>
.alert-warning {
  background-color: #fff3cd;
  border-color: #ffeaa7;
  color: #856404;
}

.incidence-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  min-width: 32px;
  padding: 0.375rem 0.75rem;
  vertical-align: middle;
}
</style>
